import React, { useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import SiteContentManager from "@/components/admin/SiteContentManager";
import SeoPageManager from "@/components/admin/SeoPageManager";
import MediaManager from "@/components/admin/MediaManager";
import MediaSeoManager from "@/components/admin/MediaSeoManager";
import NavigationManager from "@/components/admin/NavigationManager";
import CourseManager from "@/components/admin/CourseManager";
import BlogManager from "@/components/admin/BlogManager";
import GscAnalytics from "@/components/admin/GscAnalytics";
import RedirectManager from "@/components/admin/RedirectManager";
import RobotsManager from "@/components/admin/RobotsManager";
import SitemapManager from "@/components/admin/SitemapManager";
import LocalSeoManager from "@/components/admin/LocalSeoManager";
import SchemaManager from "@/components/admin/SchemaManager";
import InternalLinkManager from "@/components/admin/InternalLinkManager";
import AuditLogs from "@/components/admin/AuditLogs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Globe,
  FileText,
  Search,
  Image,
  Navigation,
  BookOpen,
  PenSquare,
  Repeat,
  Bot,
  MapPin,
  Code2,
  Link2,
  ScrollText,
  BarChart2,
  Sparkles
} from "lucide-react";

export default function SiteSeoDashboard() {
  const [activeTab, setActiveTab] = useState("site-content");

  const tabs = [
    { id: "site-content", label: "Site Content", icon: FileText, category: "Content" },
    { id: "seo-pages", label: "Page SEO & Meta", icon: Search, category: "SEO" },
    { id: "media", label: "Media Library & Replace", icon: Image, category: "Media" },
    { id: "media-seo", label: "Media SEO (Alt Tags)", icon: Sparkles, category: "Media" },
    { id: "navigation", label: "Navigation & Menus", icon: Navigation, category: "Structure" },
    { id: "courses", label: "Courses", icon: BookOpen, category: "Content" },
    { id: "blogs", label: "Blogs & Articles", icon: PenSquare, category: "Content" },
    { id: "schema", label: "Global Schema (JSON-LD)", icon: Code2, category: "SEO" },
    { id: "redirects", label: "Redirects & 404s", icon: Repeat, category: "Technical" },
    { id: "robots", label: "Robots.txt", icon: Bot, category: "Technical" },
    { id: "sitemap", label: "Sitemap XML", icon: Globe, category: "Technical" },
    { id: "local-seo", label: "Local SEO", icon: MapPin, category: "SEO" },
    { id: "gsc", label: "Search Console", icon: BarChart2, category: "SEO" },
    { id: "auto-links", label: "Auto-Links", icon: Link2, category: "SEO" },
    { id: "audit-logs", label: "Audit Logs", icon: ScrollText, category: "Technical" },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-gray-200 dark:border-white/[0.06]">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5">
              <span className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60 inline-flex">
                <Globe className="w-5 h-5" />
              </span>
              Site Content & SEO Management Suite
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-1">
              Full WordPress-style control over page content, tags, schemas, redirects, and media assets with image replacement across the entire site.
            </p>
          </div>
        </div>

        {/* Unified Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
          <div className="w-full overflow-x-auto pb-2 border-b border-slate-200/80 dark:border-zinc-800 custom-scrollbar">
            <TabsList className="bg-transparent border-0 p-0 h-auto gap-1.5 inline-flex min-w-max">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border border-transparent transition-all data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:border-emerald-200 dark:data-[state=active]:bg-emerald-950/40 dark:data-[state=active]:text-emerald-300 dark:data-[state=active]:border-emerald-800/60 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                  >
                    <Icon className="w-3.5 h-3.5" /> {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Tab Contents */}
          <div className="mt-4">
            <TabsContent value="site-content" className="mt-0 focus-visible:outline-hidden">
              <SiteContentManager />
            </TabsContent>

            <TabsContent value="seo-pages" className="mt-0 focus-visible:outline-hidden">
              <SeoPageManager />
            </TabsContent>

            <TabsContent value="media" className="mt-0 focus-visible:outline-hidden">
              <MediaManager />
            </TabsContent>

            <TabsContent value="media-seo" className="mt-0 focus-visible:outline-hidden">
              <MediaSeoManager />
            </TabsContent>

            <TabsContent value="navigation" className="mt-0 focus-visible:outline-hidden">
              <NavigationManager />
            </TabsContent>

            <TabsContent value="courses" className="mt-0 focus-visible:outline-hidden">
              <CourseManager />
            </TabsContent>

            <TabsContent value="blogs" className="mt-0 focus-visible:outline-hidden">
              <BlogManager />
            </TabsContent>

            <TabsContent value="schema" className="mt-0 focus-visible:outline-hidden">
              <SchemaManager />
            </TabsContent>

            <TabsContent value="redirects" className="mt-0 focus-visible:outline-hidden">
              <RedirectManager />
            </TabsContent>

            <TabsContent value="robots" className="mt-0 focus-visible:outline-hidden">
              <RobotsManager />
            </TabsContent>

            <TabsContent value="sitemap" className="mt-0 focus-visible:outline-hidden">
              <SitemapManager />
            </TabsContent>

            <TabsContent value="local-seo" className="mt-0 focus-visible:outline-hidden">
              <LocalSeoManager />
            </TabsContent>

            <TabsContent value="gsc" className="mt-0 focus-visible:outline-hidden">
              <GscAnalytics />
            </TabsContent>

            <TabsContent value="auto-links" className="mt-0 focus-visible:outline-hidden">
              <InternalLinkManager />
            </TabsContent>

            <TabsContent value="audit-logs" className="mt-0 focus-visible:outline-hidden">
              <AuditLogs />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
